import { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { type DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { RowMenu } from "@/components/ui/RowMenu";
import { cn } from "@/utils/cn";

export interface DataColumn<T> {
  key: string;
  label: ReactNode;
  align?: "left" | "right";
  /** Tailwind width class, e.g. "w-28". */
  width?: string;
  /**
   * A `truncate` in its cells shrinks to the room the other columns leave, rather than widen the table (walk 15's C2).
   * An auto-layout table makes a column as wide as its widest text that cannot wrap, so a `truncate` there never
   * truncated and on a phone the table scrolled sideways. For a table's one column with no width, which takes the rest.
   */
  truncates?: boolean;
  render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  empty?: ReactNode;
  /** A bar above the rows, in the table's own card: its filters and search, usually a FilterBar. None when absent. */
  toolbar?: ReactNode;
  /** A bar pinned under the scrolling rows, at the table's bottom however few rows there are: totals, usually a TotalsFooter. */
  footer?: ReactNode;
  /** Quick actions for a row, shown as its context menu (DD-045). An empty list shows no menu. */
  rowMenu?: (row: T) => DropdownMenuEntry[];
}

/** Stops a row click when a control inside the row is used. */
export function RowControls({ children, className }: { children: ReactNode; className?: string }) {
  const stop = (e: MouseEvent) => e.stopPropagation();
  return (
    <div onClick={stop} className={cn("flex items-center justify-end gap-2", className)}>
      {children}
    </div>
  );
}

/**
 * The table of a table page (DD-042): fills the remaining height and scrolls inside, with a sticky
 * header, uniform rows and no pagination, so filtering never moves the layout. Its filters (toolbar) and
 * totals (footer) are part of it, each on when passed: the toolbar above the rows, the footer under the scroll
 * area, so it stays at the bottom with one row or a hundred.
 */
export function DataTable<T>({ columns: base, rows, rowKey, onRowClick, rowClassName, empty = "Nothing here.", toolbar, footer, rowMenu }: DataTableProps<T>) {
  const th = "px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap";
  const columns: DataColumn<T>[] = rowMenu ? [...base, { key: "__menu", label: "", align: "right", width: "w-12", render: (r) => <RowMenu items={rowMenu(r)} /> }] : base;
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg overflow-hidden">
      {toolbar && <div className="shrink-0">{toolbar}</div>}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={cn(th, c.align === "right" ? "text-right" : "text-left", c.width, c.truncates && "max-w-0")}>
                  {c.key === "__menu" ? <span className="sr-only">Actions</span> : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center text-sm text-gray-500">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={rowKey(r)}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e: KeyboardEvent) => {
                          // Keys from nested controls (the row menu, inputs) are theirs, not the row's.
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(r);
                          }
                        }
                      : undefined
                  }
                  className={cn("h-14", onRowClick && "cursor-pointer hover:bg-gray-50", rowClassName?.(r))}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cn("px-4 py-2 text-sm text-gray-900", c.align === "right" ? "text-right tabular-nums" : "text-left", c.width, c.truncates && "max-w-0")}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {footer && rows.length > 0 && <div className="shrink-0">{footer}</div>}
    </div>
  );
}
